/**
 * Markdown processing utilities for CKEditor integration
 * Handles conversion between markdown and HTML formats
 */

/**
 * Convert markdown text to HTML that CKEditor can understand
 * This handles common markdown patterns that the AI generates
 */
export const markdownToHtml = (markdown: string): string => {
  if (!markdown) return "";

  let html = markdown;

  // Convert bullet points (• text) to clean HTML list structure
  // Remove bullet symbols and let CKEditor handle the styling
  const bulletPattern = /^(\s*)•\s+(.+)$/gm;
  const matches = html.match(bulletPattern);

  if (matches) {
    // Group consecutive bullet points into a clean unordered list
    html = html.replace(/((^\s*•\s+.+$\n?)+)/gm, (match) => {
      const listItems = match
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => {
          // Remove the bullet symbol completely so CKEditor doesn't create double bullets
          const content = line.replace(/^\s*•\s+/, "").trim();
          return `<li>${content}</li>`;
        })
        .join("");
      return `<ul>${listItems}</ul>`;
    });
  }

  // Additional safety check: remove any remaining bullet symbols that might cause double bullets
  // This handles cases where bullet symbols might appear in already converted HTML
  html = html.replace(/<li>•\s*(.+?)<\/li>/g, "<li>$1</li>");
  html = html.replace(/<li>\s*•\s*(.+?)<\/li>/g, "<li>$1</li>");

  // Convert **bold** to <strong>
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Convert *italic* to <em>
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");

  // Convert fenced code blocks (```) to <pre><code>
  html = html.replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>");

  // Convert `code` to <code>
  html = html.replace(/`(.*?)`/g, "<code>$1</code>");

  // Convert line breaks to <br> (but not if already in HTML tags)
  html = html.replace(/\n(?![^<]*>)/g, "<br>");

  // Convert markdown headers
  html = html.replace(/^### (.*$)/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gm, "<h1>$1</h1>");

  // Convert blockquotes
  html = html.replace(/^> (.+$)/gm, "<blockquote>$1</blockquote>");

  // Convert numbered lists
  const numberedListPattern = /^(\s*)\d+\.\s+(.+)$/gm;
  const numberedMatches = html.match(numberedListPattern);

  if (numberedMatches) {
    html = html.replace(/((^\s*\d+\.\s+.+$\n?)+)/gm, (match) => {
      const listItems = match
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => {
          const content = line.replace(/^\s*\d+\.\s+/, "").trim();
          return `<li>${content}</li>`;
        })
        .join("");
      return `<ol>${listItems}</ol>`;
    });
  }

  // Clean up extra br tags around block elements
  html = html.replace(/<br>\s*(<\/?(ul|ol|li|h[1-6]|blockquote)>)/g, "$1");
  html = html.replace(/(<\/?(ul|ol|li|h[1-6]|blockquote)>)\s*<br>/g, "$1");

  // Final cleanup: remove any double bullets that might have been created
  // This handles edge cases where bullets appear twice
  html = html.replace(/•\s*•\s*/g, "• "); // Replace "• • " with just "• "
  html = html.replace(/^\s*•\s*•\s*/gm, "• "); // Replace line-starting "• • " with just "• "

  return html;
};

/**
 * Convert HTML back to markdown (for editing)
 * This is useful when switching between view and edit modes
 */
export const htmlToMarkdown = (html: string): string => {
  if (!html) return "";

  let markdown = html;

  // Convert HTML lists back to markdown
  markdown = markdown.replace(/<ul>[\s\S]*?<\/ul>/g, (match) => {
    const content = match.replace(/<\/?ul>/g, "");
    const items = content.match(/<li>(.*?)<\/li>/g);
    if (!items) return match;

    const markdownItems = items
      .map((item) => {
        const text = item.replace(/<\/?li>/g, "").trim();
        return `• ${text}`;
      })
      .join("\n");

    return markdownItems;
  });

  markdown = markdown.replace(/<ol>[\s\S]*?<\/ol>/g, (match) => {
    const content = match.replace(/<\/?ol>/g, "");
    const items = content.match(/<li>(.*?)<\/li>/g);
    if (!items) return match;

    const markdownItems = items
      .map((item, index) => {
        const text = item.replace(/<\/?li>/g, "").trim();
        return `${index + 1}. ${text}`;
      })
      .join("\n");

    return markdownItems;
  });

  // Convert HTML formatting back to markdown
  markdown = markdown.replace(/<strong>(.*?)<\/strong>/g, "**$1**");
  markdown = markdown.replace(/<em>(.*?)<\/em>/g, "*$1*");

  // Convert code blocks back to markdown
  markdown = markdown.replace(
    /<pre><code>([\s\S]*?)<\/code><\/pre>/g,
    "```$1```"
  );

  // Convert inline code back to markdown
  markdown = markdown.replace(/<code>(.*?)<\/code>/g, "`$1`");

  // Convert headers
  markdown = markdown.replace(/<h1>(.*?)<\/h1>/g, "# $1");
  markdown = markdown.replace(/<h2>(.*?)<\/h2>/g, "## $1");
  markdown = markdown.replace(/<h3>(.*?)<\/h3>/g, "### $1");

  // Convert blockquotes
  markdown = markdown.replace(/<blockquote>(.*?)<\/blockquote>/g, "> $1");

  // Convert br tags back to line breaks
  markdown = markdown.replace(/<br\s*\/?>/g, "\n");

  // Clean up extra whitespace
  markdown = markdown.replace(/\n\s*\n\s*\n/g, "\n\n");

  return markdown.trim();
};

/**
 * Check if content appears to be markdown format
 */
export const isMarkdownContent = (content: string): boolean => {
  if (!content) return false;

  // Look for common markdown patterns
  const markdownPatterns = [
    /^•\s+/m, // Bullet points
    /\*\*.*?\*\*/, // Bold text
    /\*.*?\*/, // Italic text
    /`.*?`/, // Inline code
    /```[\s\S]*?```/, // Fenced code blocks
    /^#+\s+/m, // Headers
    /^\d+\.\s+/m, // Numbered lists
    /^>\s+/m, // Blockquotes
  ];

  return markdownPatterns.some((pattern) => pattern.test(content));
};

/**
 * Process AI-generated usecase description to ensure proper formatting
 */
export const processUsecaseDescription = (description: string): string => {
  if (!description) return "";

  // If it looks like markdown, convert it to HTML
  if (isMarkdownContent(description)) {
    return markdownToHtml(description);
  }

  // If it's plain text but has bullet-like structure, convert to proper HTML list
  if (
    description.includes("•") ||
    description.includes("\n-") ||
    description.includes("\n*")
  ) {
    return markdownToHtml(description);
  }

  return description;
};
